import { selectCurriculumHeaderInfo } from '@components/leerplan/section/header/leerplanHeaderSelectors';
import { createAction, PayloadAction } from '@reduxjs/toolkit';
import { waitForSaga } from '@utils/sagaUtils';
import { call, debounce, select, takeEvery } from 'redux-saga/effects';
import { selectFullAnnotatedCurriculumViewModel } from '@store/curriculum/curriculumSelectors';
import ReactGA from 'react-ga4';
import { saveLeidraadChoices } from '@store/userAndSchool/usersAndSchoolState';
import { RootState } from '@store/storeSetup';
import { selectUserRoleForTracking } from '@store/userAndSchool/userAndSchoolSelectors';

export const trackCurriculumOpen = createAction<void>('tracking/trackCurriculumOpen');
export const trackIncludeSubItem = createAction<{ type: string; include: boolean }>(
  'tracking/trackIncludeSubItem'
);
export const trackDistributeGoal = createAction<boolean>('tracking/trackDistributeGoal');
export const trackCreateNonDerived = createAction<void>('tracking/trackCreateNonDerived');
export const trackShowHideOptionalElements = createAction<void>(
  'tracking/trackShowHideOptionalElements'
);
export const trackNavigateRelatedGoal = createAction<void>('tracking/trackNavigateRelatedGoal');
export const trackTermClicked = createAction<void>('tracking/trackTermClicked');
export const trackReadRichTextTab = createAction<string>('tracking/trackReadRichTextTab');

export const trackGoalMoved = createAction<void>('tracking/trackGoalMoved');
export const trackGoalAdded = createAction<void>('tracking/trackGoalAdded');
export const trackCustomGoalAdded = createAction<void>('tracking/trackCustomGoalAdded');
export const trackCustomGoalChanged = createAction<void>('tracking/trackCustomGoalChanged');
export const trackSectionAdded = createAction<void>('tracking/trackSectionAdded');

export const trackSelectorUsage = createAction<{ type: string }>('tracking/trackSelectorUsage');
export const trackChangeSchoolyear = createAction<void>('tracking/trackChangeSchoolyear');
export const trackTryChangeFutureSchoolyear = createAction<void>(
  'tracking/trackTryChangeFutureSchoolyear'
);
export const trackDeleteLeerplan = createAction<{
  delete: boolean;
  isNonderived: boolean;
  curriculumType: string;
}>('tracking/trackDeleteLeerplan');

export const trackFilterLeerplan = createAction<{ filter: string }>('tracking/trackFilterLeerplan');

export const trackFilterActivityPlan = createAction<{ filter: string }>(
  'tracking/trackFilterActivityPlan'
);
export const trackCreateActivityPlan = createAction<void>('tracking/trackCreateActivityPlan');
export const trackChangeActivityPlan = createAction<void>('tracking/trackChangeActivityPlan');
export const trackDeleteActivityPlan = createAction<void>('tracking/trackDeleteActivityPlan');
export const trackRestoreActivityPlan = createAction<void>('tracking/trackRestoreActivityPlan');
export const trackCopyActivityPlan = createAction<void>('tracking/trackCopyActivityPlan');
export const trackCopyActivityPlanCancelled = createAction<void>(
  'tracking/trackCopyActivityPlanCancelled'
);
export const trackCreateActivity = createAction<void>('tracking/trackCreateActivity');
export const trackCreateActivityCancelled = createAction<void>(
  'tracking/trackCreateActivityCancelled'
);
export const trackChangeActivity = createAction<void>('tracking/trackChangeActivity');
export const trackChangeActivityCancelled = createAction<void>(
  'tracking/trackChangeActivityCancelled'
);
export const trackDeleteActivity = createAction<void>('tracking/trackDeleteActivity');
export const trackMoveActivity = createAction<void>('tracking/trackMoveActivity');
export const trackPrintActivityPlan = createAction<{ viewType: string }>(
  'tracking/trackPrintActivityPlan'
);
export const trackViewGoalDistribution = createAction<void>('tracking/trackViewGoalDistribution');
export const trackChangeVisibleClasses = createAction<void>('tracking/trackChangeVisibleClasses');
export const trackAddGoalToActivity = createAction<void>('tracking/trackAddGoalToActivity');
export const trackRemoveGoalFromActivity = createAction<void>(
  'tracking/trackRemoveGoalFromActivity'
);
export const trackUseCurriculumSelector = createAction<void>('tracking/trackUseCurriculumSelector');

const camelToSnakeCase = (str) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

function* sendGAEvent(eventCategory: string, eventAction: string, additionalParams: object = {}) {
  const schoolyear = yield select((state: RootState) => state.userAndSchools.currentSchoolyear);
  const userRole = yield select((state: RootState) => selectUserRoleForTracking(state));
  ReactGA.send({
    hitType: 'event',
    eventCategory,
    eventAction,
    schoolyear,
    user_role: userRole,
    ...additionalParams,
  });
}

function* trackCurriculumOpenSaga() {
  yield call(waitForSaga, selectFullAnnotatedCurriculumViewModel);
  const headerInfo = yield select(selectCurriculumHeaderInfo);
  yield sendGAEvent('leerplannen', 'open_curriculum', {
    eventLabel: headerInfo.curriculumTitle,
    curriculum_type: headerInfo.curriculumType,
    non_derived: headerInfo.isNonderived,
  });
}

function* trackIncludeSubItemSaga(action: PayloadAction<{ type: string; include: boolean }>) {
  const headerInfo = yield select(selectCurriculumHeaderInfo);
  const event = `${
    action.payload.include ? 'include' : 'exclude'
  }_${action.payload.type.toLowerCase()}`;
  yield sendGAEvent('leerplannen', event, {
    curriculum_type: headerInfo.curriculumType,
    non_derived: headerInfo.isNonderived,
  });
}

function* trackDistributeGoalSaga(action: PayloadAction<boolean>) {
  yield sendGAEvent('leerplannen', `${action.payload ? '' : 'remove_'}distribute_goal`);
}

function* trackCreateNonDerivedSaga() {
  yield sendGAEvent('leerplannen', 'create_non_derived');
}

function* trackShowHideOptionalElementsSaga() {
  const headerInfo = yield select(selectCurriculumHeaderInfo);
  yield sendGAEvent('leerplannen', 'show_hide_optional_elements', {
    curriculum_type: headerInfo.curriculumType,
    non_derived: headerInfo.isNonderived,
  });
}

function* trackNavigateRelatedGoalSaga() {
  const headerInfo = yield select(selectCurriculumHeaderInfo);
  yield sendGAEvent('leerplannen', 'navigate_related_goal', {
    curriculum_type: headerInfo.curriculumType,
    non_derived: headerInfo.isNonderived,
  });
}

function* trackTermClickedSaga() {
  const headerInfo = yield select(selectCurriculumHeaderInfo);
  yield sendGAEvent('leerplannen', 'term_clicked', {
    curriculum_type: headerInfo.curriculumType,
    non_derived: headerInfo.isNonderived,
  });
}

function* trackReadRichTextTabSaga(action: PayloadAction<string>) {
  const headerInfo = yield select(selectCurriculumHeaderInfo);
  yield sendGAEvent('leerplannen', `read_rich_text_tab_${action.payload}`, {
    curriculum_type: headerInfo.curriculumType,
    non_derived: headerInfo.isNonderived,
  });
}

function* trackGoalMovedSaga() {
  const headerInfo = yield select(selectCurriculumHeaderInfo);
  yield sendGAEvent('leerplannen', 'goal_moved', {
    curriculum_type: headerInfo.curriculumType,
    non_derived: headerInfo.isNonderived,
  });
}

function* trackGoalAddedSaga() {
  const headerInfo = yield select(selectCurriculumHeaderInfo);
  yield sendGAEvent('leerplannen', 'goal_added', {
    curriculum_type: headerInfo.curriculumType,
    non_derived: headerInfo.isNonderived,
  });
}

function* trackCustomGoalAddedSaga() {
  const headerInfo = yield select(selectCurriculumHeaderInfo);
  yield sendGAEvent('leerplannen', 'custom_goal_added', {
    curriculum_type: headerInfo.curriculumType,
    non_derived: headerInfo.isNonderived,
  });
}

function* trackCustomGoalChangedSaga() {
  const headerInfo = yield select(selectCurriculumHeaderInfo);
  yield sendGAEvent('leerplannen', 'custom_goal_changed', {
    curriculum_type: headerInfo.curriculumType,
    non_derived: headerInfo.isNonderived,
  });
}

function* trackSectionAddedSaga() {
  const headerInfo = yield select(selectCurriculumHeaderInfo);
  yield sendGAEvent('leerplannen', 'section_added', {
    curriculum_type: headerInfo.curriculumType,
    non_derived: headerInfo.isNonderived,
  });
}

function* trackSelectorUsageSaga(action: PayloadAction<{ type: string }>) {
  yield sendGAEvent('selector', `select_${action.payload.type}`);
}

function* trackChangeLeidraadSaga() {
  yield sendGAEvent('home', 'modify_leidraad');
}

function* trackChangeSchoolyearSaga() {
  yield sendGAEvent('home', 'change_schoolyear');
}

function* trackTryChangeFutureSchoolyearSaga() {
  yield sendGAEvent('home', 'try_change_future_schoolyear');
}

function* trackDeleteLeerplanSaga(
  action: PayloadAction<{ delete: boolean; isNonderived: boolean; curriculumType: string }>
) {
  yield sendGAEvent(
    'leerplannen',
    `${action.payload.delete ? 'delete' : 'cancel_delete'}_curriculum`,
    {
      curriculum_type: action.payload.curriculumType,
      non_derived: action.payload.isNonderived,
    }
  );
}

function* trackFilterLeerplanSaga(action: PayloadAction<{ filter: string }>) {
  yield sendGAEvent('home', `filter_curriculum_${camelToSnakeCase(action.payload.filter)}`);
}

function* trackFilterActivityPlanSaga(action: PayloadAction<{ filter: string }>) {
  yield sendGAEvent('home', `filter_activity_plan_${camelToSnakeCase(action.payload.filter)}`);
}

function* trackCreateActivityPlanSaga() {
  yield sendGAEvent('home', 'create_activity_plan');
}

function* trackChangeActivityPlanSaga() {
  yield sendGAEvent('home', 'change_activity_plan');
}

function* trackDeleteActivityPlanSaga() {
  yield sendGAEvent('home', 'delete_activity_plan');
}

function* trackRestoreActivityPlanSaga() {
  yield sendGAEvent('home', 'restore_activity_plan');
}

function* trackCopyActivityPlanSaga() {
  yield sendGAEvent('home', 'copy_activity_plan');
}

function* trackCopyActivityPlanCancelledSaga() {
  yield sendGAEvent('home', 'cancel_copy_activity_plan');
}

function* trackCreateActivitySaga() {
  yield sendGAEvent('vorderingsplannen', 'create_activity');
}

function* trackCreateActivityCancelledSaga() {
  yield sendGAEvent('vorderingsplannen', 'cancel_create_activity');
}

function* trackChangeActivitySaga() {
  yield sendGAEvent('vorderingsplannen', 'change_activity');
}

function* trackChangeActivityCancelledSaga() {
  yield sendGAEvent('vorderingsplannen', 'cancel_change_activity');
}

function* trackDeleteActivitySaga() {
  yield sendGAEvent('vorderingsplannen', 'delete_activity');
}

function* trackMoveActivitySaga() {
  yield sendGAEvent('vorderingsplannen', 'move_activity');
}

function* trackPrintActivityPlanSaga() {
  yield sendGAEvent('vorderingsplannen', 'print_activity_plan');
}

function* trackViewGoalDistributionSaga() {
  yield sendGAEvent('vorderingsplannen', 'view_goal_distribution');
}

function* trackChangeVisibleClassesSaga() {
  yield sendGAEvent('vorderingsplannen', 'change_visible_classes');
}

function* trackAddGoalToActivitySaga() {
  yield sendGAEvent('vorderingsplannen', 'add_goal_to_activity');
}

function* trackRemoveGoalFromActivitySaga() {
  yield sendGAEvent('vorderingsplannen', 'remove_goal_from_activity');
}

function* trackUseCurriculumSelectorSaga() {
  yield sendGAEvent('vorderingsplannen', 'use_curriculum_selector');
}

export function* watchTrackingSaga() {
  yield takeEvery(trackCurriculumOpen, trackCurriculumOpenSaga);
  yield takeEvery(trackIncludeSubItem, trackIncludeSubItemSaga);
  yield takeEvery(trackDistributeGoal, trackDistributeGoalSaga);
  yield takeEvery(trackCreateNonDerived, trackCreateNonDerivedSaga);
  yield takeEvery(trackShowHideOptionalElements, trackShowHideOptionalElementsSaga);
  yield takeEvery(trackNavigateRelatedGoal, trackNavigateRelatedGoalSaga);
  yield takeEvery(trackTermClicked, trackTermClickedSaga);
  yield takeEvery(trackReadRichTextTab, trackReadRichTextTabSaga);
  yield takeEvery(trackGoalMoved, trackGoalMovedSaga);
  yield takeEvery(trackGoalAdded, trackGoalAddedSaga);
  yield takeEvery(trackCustomGoalAdded, trackCustomGoalAddedSaga);
  yield takeEvery(trackCustomGoalChanged, trackCustomGoalChangedSaga);
  yield takeEvery(trackSectionAdded, trackSectionAddedSaga);
  yield takeEvery(trackSelectorUsage, trackSelectorUsageSaga);
  yield takeEvery(saveLeidraadChoices, trackChangeLeidraadSaga);
  yield takeEvery(trackChangeSchoolyear, trackChangeSchoolyearSaga);
  yield takeEvery(trackTryChangeFutureSchoolyear, trackTryChangeFutureSchoolyearSaga);
  yield takeEvery(trackDeleteLeerplan, trackDeleteLeerplanSaga);
  yield debounce(2000, trackFilterLeerplan, trackFilterLeerplanSaga);
  yield debounce(2000, trackFilterActivityPlan, trackFilterActivityPlanSaga);
  yield takeEvery(trackCreateActivityPlan, trackCreateActivityPlanSaga);
  yield takeEvery(trackChangeActivityPlan, trackChangeActivityPlanSaga);
  yield takeEvery(trackDeleteActivityPlan, trackDeleteActivityPlanSaga);
  yield takeEvery(trackRestoreActivityPlan, trackRestoreActivityPlanSaga);
  yield takeEvery(trackCopyActivityPlan, trackCopyActivityPlanSaga);
  yield takeEvery(trackCopyActivityPlanCancelled, trackCopyActivityPlanCancelledSaga);
  yield takeEvery(trackCreateActivity, trackCreateActivitySaga);
  yield takeEvery(trackCreateActivityCancelled, trackCreateActivityCancelledSaga);
  yield takeEvery(trackChangeActivity, trackChangeActivitySaga);
  yield takeEvery(trackChangeActivityCancelled, trackChangeActivityCancelledSaga);
  yield takeEvery(trackDeleteActivity, trackDeleteActivitySaga);
  yield takeEvery(trackMoveActivity, trackMoveActivitySaga);
  yield takeEvery(trackPrintActivityPlan, trackPrintActivityPlanSaga);
  yield takeEvery(trackViewGoalDistribution, trackViewGoalDistributionSaga);
  yield takeEvery(trackChangeVisibleClasses, trackChangeVisibleClassesSaga);
  yield takeEvery(trackAddGoalToActivity, trackAddGoalToActivitySaga);
  yield takeEvery(trackRemoveGoalFromActivity, trackRemoveGoalFromActivitySaga);
  yield takeEvery(trackUseCurriculumSelector, trackUseCurriculumSelectorSaga);
}
